// ProvasView — provas agendadas: lista, criar, editar, cockpit, ver respostas, anular

import { signal } from "@preact/signals";
import { useEffect, useMemo, useState } from "preact/hooks";
import {
  AlertCircle,
  Calendar,
  CheckCircle,
  ClipboardList,
  Clock,
  Eye,
  Hourglass,
  Loader2,
  Pencil,
  Plus,
  Save,
  Search,
  Trash2,
  Users,
  X
} from "lucide-react";
import {
  api,
  ApiError,
  type BankRow,
  type DisciplinaRow,
  type DisciplinaEvaluationItem,
  type ExamAnswers,
  type ExamMonitor,
  type ExamRow
} from "../api";
import { Switch } from "@app/ui/components/switch";
import { ProvaAgendadaDetailView, provaDetailTarget } from "./ProvaAgendadaDetailView";
import { openAlert } from "../ConfirmPromptModal";
import { friendlyError } from "@app/ui/lib/friendly-error";

type ExamPhase = "lobby" | "live" | "closed" | "released";

const exams = signal<ExamRow[] | null>(null);
const banks = signal<BankRow[]>([]);
const disciplinas = signal<DisciplinaRow[]>([]);
const phase = signal<"loading" | "ready" | "error">("loading");
const errMsg = signal<string | null>(null);
const search = signal<string>("");
const filterPhase = signal<"all" | ExamPhase>("all");
const editing = signal<ExamRow | null>(null);
const creating = signal<boolean>(false);
const deleting = signal<ExamRow | null>(null);
const monitorOpen = signal<ExamRow | null>(null);
const answersOpen = signal<ExamRow | null>(null);

function phaseOf(e: ExamRow): ExamPhase {
  const now = Date.now();
  const startsAt = e.startsAt ? Date.parse(e.startsAt) : Number.POSITIVE_INFINITY;
  const endsAt = e.endsAt ? Date.parse(e.endsAt) : Number.NEGATIVE_INFINITY;
  if (startsAt > now) return "lobby";
  if (endsAt >= now) return "live";
  if (e.releasedAt) return "released";
  return "closed";
}

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    // Disciplinas em paralelo — usadas pelo seletor de vínculo
    // prova↔avaliação composta no CreateModal/EditModal.
    const [er, br, dr] = await Promise.all([
      api.listExams(),
      api.listBanks(),
      api.listDisciplinas()
    ]);
    exams.value = er.exams;
    banks.value = br.banks.filter((b) => !b.archivedAt);
    disciplinas.value = dr.disciplinas;
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = friendlyError(e);
    phase.value = "error";
  }
}

export function ProvasView() {
  useEffect(() => {
    load();
  }, []);
  if (provaDetailTarget.value) {
    return (
      <ProvaAgendadaDetailView
        target={provaDetailTarget.value}
        onClose={() => (provaDetailTarget.value = null)}
        onRefreshList={() => void load()}
      />
    );
  }
  if (phase.value === "loading") {
    return (
      <p class="flex items-center gap-2 text-muted-foreground">
        <Loader2 className="size-4 animate-spin" /> Carregando…
      </p>
    );
  }
  if (phase.value === "error") {
    return (
      <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm">
        <AlertCircle className="mr-2 inline size-4 text-destructive" />
        {errMsg.value}
      </div>
    );
  }
  const rows = exams.value ?? [];
  const q = search.value.toLowerCase().trim();
  const visible = useMemo(() => {
    return rows.filter((e) => {
      if (q && !e.title.toLowerCase().includes(q)) return false;
      if (filterPhase.value !== "all" && phaseOf(e) !== filterPhase.value) return false;
      return true;
    });
  }, [rows, q, filterPhase.value]);

  const counts = useMemo(() => {
    const c = { lobby: 0, live: 0, closed: 0, released: 0 };
    for (const e of rows) c[phaseOf(e)]++;
    return c;
  }, [rows]);

  return (
    <div class="space-y-4">
      <header class="flex items-start justify-between gap-3">
        <div>
          <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <ClipboardList className="size-6 text-primary" />
            Provas agendadas
          </h1>
          <p class="mt-1 text-sm text-muted-foreground">
            Crie provas com janela, peso por questão e snapshot do banco. Monitore ao vivo
            no Cockpit e revise respostas depois.
          </p>
        </div>
        <button
          type="button"
          onClick={() => (provaDetailTarget.value = { mode: "create" })}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110"
        >
          <Plus width="1.1em" height="1.1em" />
          Nova prova
        </button>
      </header>

      <div class="flex flex-wrap items-center gap-2 rounded-md border border-border bg-card/40 px-3 py-2">
        <div class="relative min-w-[240px] flex-1">
          <Search
            width="1em"
            height="1em"
            className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
          />
          <input
            type="search"
            value={search.value}
            onInput={(e) => (search.value = (e.target as HTMLInputElement).value)}
            placeholder="Buscar título…"
            class="w-full rounded-md border border-border bg-background pl-8 pr-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </div>
        {(["all", "lobby", "live", "closed", "released"] as const).map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => (filterPhase.value = p)}
            class={`inline-flex items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-semibold transition-colors ${
              filterPhase.value === p
                ? "border-primary/50 bg-primary/10 text-primary"
                : "border-border bg-background/50 text-muted-foreground hover:bg-accent"
            }`}
          >
            {p === "all" ? "Todas" : p === "lobby" ? "Agendadas" : p === "live" ? "Ao vivo" : p === "closed" ? "Encerradas" : "Liberadas"}
            {p !== "all" && (
              <span class="rounded bg-accent/60 px-1 text-[10px] tabular-nums">
                {counts[p]}
              </span>
            )}
          </button>
        ))}
      </div>

      {visible.length === 0 ? (
        <div class="rounded-md border border-dashed border-border bg-card/30 px-4 py-8 text-center text-sm text-muted-foreground">
          Nenhuma prova encontrada.
        </div>
      ) : (
        <div class="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {visible.map((e) => (
            <ExamCard key={e.id} exam={e} />
          ))}
        </div>
      )}

      {editing.value && <EditModal exam={editing.value} />}
      {creating.value && <CreateModal />}
      {deleting.value && <DeleteModal exam={deleting.value} />}
      {monitorOpen.value && <MonitorModal exam={monitorOpen.value} />}
      {answersOpen.value && <AnswersModal exam={answersOpen.value} />}
    </div>
  );
}

function ExamCard({ exam }: { exam: ExamRow }) {
  const p = phaseOf(exam);
  const tone =
    p === "live"
      ? "border-amber-500/60 bg-amber-500/10 ring-1 ring-amber-500/30"
      : p === "lobby"
        ? "border-primary/40 bg-primary/5"
        : p === "released"
          ? "border-violet-500/40 bg-violet-500/5"
          : "border-border bg-card/40";
  return (
    <div class={`space-y-2 rounded-md border p-4 ${tone}`}>
      <div class="flex items-start justify-between gap-2">
        <div class="min-w-0 flex-1">
          <h3 class="truncate text-sm font-bold">{exam.title}</h3>
          {exam.startsAt && (
            <p class="font-mono text-[10px] text-muted-foreground">
              {new Date(exam.startsAt).toLocaleString("pt-BR")}
              {exam.endsAt && ` → ${new Date(exam.endsAt).toLocaleString("pt-BR")}`}
            </p>
          )}
        </div>
        <PhaseBadge phase={p} />
      </div>
      <div class="flex flex-wrap items-center gap-1.5 pt-2">
        <button
          type="button"
          onClick={() =>
            (provaDetailTarget.value = { mode: "edit", examId: exam.id })
          }
          class="inline-flex items-center gap-1 rounded-md border border-border bg-background px-2 py-1 text-[10px] font-semibold hover:bg-accent"
        >
          {p === "live" ? (
            <>
              <Eye width="0.9em" height="0.9em" /> Cockpit
            </>
          ) : p === "closed" || p === "released" ? (
            <>
              <ClipboardList width="0.9em" height="0.9em" /> Resultados
            </>
          ) : (
            <>
              <Pencil width="0.9em" height="0.9em" /> Abrir
            </>
          )}
        </button>
        {p === "lobby" && (
          <button
            type="button"
            onClick={() => (deleting.value = exam)}
            title="Excluir"
            class="inline-flex items-center gap-1 rounded-md border border-red-500/40 bg-red-500/5 px-2 py-1 text-[10px] font-semibold text-red-600 hover:bg-red-500/10 dark:text-red-400"
          >
            <Trash2 width="0.9em" height="0.9em" />
          </button>
        )}
      </div>
    </div>
  );
}

function PhaseBadge({ phase: p }: { phase: ExamPhase }) {
  const map = {
    lobby: {
      cls: "bg-primary/15 text-primary ring-primary/30",
      label: "AGENDADA",
      Icon: Hourglass
    },
    live: {
      cls: "bg-amber-500/20 text-amber-700 ring-amber-500/40 dark:text-amber-300",
      label: "EM CURSO",
      Icon: Clock
    },
    closed: {
      cls: "bg-muted text-muted-foreground ring-border",
      label: "ENCERRADA",
      Icon: CheckCircle
    },
    released: {
      cls: "bg-violet-500/15 text-violet-700 ring-violet-500/30 dark:text-violet-300",
      label: "LIBERADA",
      Icon: CheckCircle
    }
  } as const;
  const m = map[p];
  return (
    <span class={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ring-1 ${m.cls}`}>
      <m.Icon width="0.9em" height="0.9em" />
      {m.label}
    </span>
  );
}

function CreateModal() {
  const [title, setTitle] = useState("");
  const [bankId, setBankId] = useState("");
  const [startsAt, setStartsAt] = useState("");
  const [endsAt, setEndsAt] = useState("");
  const [durationMinutes, setDurationMinutes] = useState<string>("");
  const [saving, setSaving] = useState(false);
  // Vínculo prova↔avaliação composta da disciplina. Três modos:
  //  "none"     → não vincula, prova fica solta
  //  "existing" → vincula a item já criado na avaliação composta
  //  "new"      → cria item novo (kind=prova) já apontando pra esta prova
  const [linkMode, setLinkMode] = useState<"none" | "existing" | "new">("none");
  const [linkDisciplinaId, setLinkDisciplinaId] = useState<string>("");
  const [linkItemId, setLinkItemId] = useState<string>("");
  const [linkNewTitle, setLinkNewTitle] = useState<string>("");
  const [linkNewWeight, setLinkNewWeight] = useState<string>("");
  const [disciplinaItems, setDisciplinaItems] = useState<DisciplinaEvaluationItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);

  // Quando muda a disciplina, busca os itens da avaliação composta dela.
  // Filtra os itens kind="prova" sem assessmentId (livres pra serem vinculados).
  useEffect(() => {
    if (!linkDisciplinaId || linkMode === "none") {
      setDisciplinaItems([]);
      return;
    }
    setLoadingItems(true);
    api.getDisciplinaEvaluation(linkDisciplinaId)
      .then((r) => setDisciplinaItems(r.items))
      .catch(() => setDisciplinaItems([]))
      .finally(() => setLoadingItems(false));
  }, [linkDisciplinaId, linkMode]);

  const freeItems = disciplinaItems.filter(
    (it) => it.kind === "prova" && !it.assessmentId
  );

  async function go() {
    if (!title.trim() || !bankId || !startsAt || !endsAt) return;
    setSaving(true);
    try {
      const payload: any = {
        title: title.trim(),
        bankId,
        startsAt: new Date(startsAt).toISOString(),
        endsAt: new Date(endsAt).toISOString(),
        durationMinutes: durationMinutes ? Number(durationMinutes) : null
      };
      // Anexa o vínculo de avaliação conforme o modo selecionado.
      if (linkMode === "existing" && linkItemId) {
        payload.evaluationItemId = linkItemId;
      } else if (linkMode === "new" && linkDisciplinaId && linkNewTitle.trim()) {
        const w = Number(linkNewWeight);
        payload.evaluationItem = {
          disciplinaId: linkDisciplinaId,
          title: linkNewTitle.trim(),
          weight: Number.isFinite(w) && w > 0 ? w : 1
        };
      }
      await api.createExam(payload);
      creating.value = false;
      await load();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal title="Nova prova agendada" onClose={() => (creating.value = false)}>
      <Field label="Título *">
        <input
          type="text"
          value={title}
          onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
          autoFocus
          aria-label="Título"
          class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
        />
      </Field>
      <Field label="Banco fonte *">
        <select
          value={bankId}
          onChange={(e) => setBankId((e.target as HTMLSelectElement).value)}
          aria-label="Banco"
          class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
        >
          <option value="">— Escolha um banco —</option>
          {banks.value.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name} ({b.questionCount} questões)
            </option>
          ))}
        </select>
      </Field>
      <div class="grid grid-cols-2 gap-2">
        <Field label="Início *">
          <input
            type="datetime-local"
            value={startsAt}
            onInput={(e) => setStartsAt((e.target as HTMLInputElement).value)}
            aria-label="Início"
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </Field>
        <Field label="Fim *">
          <input
            type="datetime-local"
            value={endsAt}
            onInput={(e) => setEndsAt((e.target as HTMLInputElement).value)}
            aria-label="Fim"
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          />
        </Field>
      </div>
      <Field label="Duração máxima (min) — opcional">
        <input
          type="number"
          value={durationMinutes}
          onInput={(e) => setDurationMinutes((e.target as HTMLInputElement).value)}
          min="0"
          aria-label="Duração"
          placeholder="Ex: 60"
          class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
        />
      </Field>

      {/* === Vínculo com avaliação composta da disciplina (opcional) === */}
      <div class="rounded-md border border-border bg-background/40 p-3 space-y-3">
        <div>
          <div class="text-xs font-semibold">Avaliação na disciplina</div>
          <p class="text-[11px] text-muted-foreground">
            Liga essa prova a um item da avaliação composta. A nota do aluno alimenta
            automaticamente a média da disciplina.
          </p>
        </div>
        <Field label="Modo">
          <select
            value={linkMode}
            onChange={(e) => {
              const v = (e.target as HTMLSelectElement).value as "none" | "existing" | "new";
              setLinkMode(v);
              if (v === "none") {
                setLinkDisciplinaId("");
                setLinkItemId("");
              }
            }}
            aria-label="Modo de vínculo"
            class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
          >
            <option value="none">Não vincular</option>
            <option value="existing">Vincular a item existente</option>
            <option value="new">Criar novo item na avaliação</option>
          </select>
        </Field>
        {linkMode !== "none" && (
          <Field label="Disciplina">
            <select
              value={linkDisciplinaId}
              onChange={(e) => {
                setLinkDisciplinaId((e.target as HTMLSelectElement).value);
                setLinkItemId("");
              }}
              aria-label="Disciplina"
              class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
            >
              <option value="">— Selecione —</option>
              {disciplinas.value.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                  {d.code ? ` (${d.code})` : ""}
                </option>
              ))}
            </select>
          </Field>
        )}
        {linkMode === "existing" && linkDisciplinaId && (
          <Field label="Item da avaliação (kind=prova, não vinculado)">
            <select
              value={linkItemId}
              onChange={(e) => setLinkItemId((e.target as HTMLSelectElement).value)}
              disabled={loadingItems || freeItems.length === 0}
              aria-label="Item de avaliação"
              class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary disabled:opacity-50"
            >
              <option value="">
                {loadingItems
                  ? "Carregando itens…"
                  : freeItems.length === 0
                    ? "Nenhum item livre nesta disciplina"
                    : "— Selecione —"}
              </option>
              {freeItems.map((it) => (
                <option key={it.id} value={it.id}>
                  {it.title} (peso {Number(it.weight)})
                </option>
              ))}
            </select>
          </Field>
        )}
        {linkMode === "new" && linkDisciplinaId && (
          <div class="grid grid-cols-3 gap-2">
            <div class="col-span-2">
              <Field label="Título do item">
                <input
                  type="text"
                  value={linkNewTitle}
                  onInput={(e) => setLinkNewTitle((e.target as HTMLInputElement).value)}
                  placeholder="Ex: Prova 1"
                  aria-label="Título do item"
                  class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
                />
              </Field>
            </div>
            <Field label="Peso (0-10)">
              <input
                type="number"
                value={linkNewWeight}
                onInput={(e) => setLinkNewWeight((e.target as HTMLInputElement).value)}
                min="0"
                max="10"
                step="0.5"
                placeholder="1"
                aria-label="Peso"
                class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
              />
            </Field>
          </div>
        )}
      </div>

      <p class="text-[11px] text-muted-foreground">
        Pesos são distribuídos igualmente entre as questões (soma 10). O snapshot do banco
        é tirado automaticamente no início da prova.
      </p>
      <ModalFooter>
        <button
          type="button"
          onClick={() => (creating.value = false)}
          disabled={saving}
          class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={go}
          disabled={saving || !title.trim() || !bankId || !startsAt || !endsAt}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
        >
          {saving ? <Loader2 className="size-3.5 animate-spin" /> : <Plus width="1em" height="1em" />}
          Criar
        </button>
      </ModalFooter>
    </Modal>
  );
}

function EditModal({ exam }: { exam: ExamRow }) {
  const [title, setTitle] = useState(exam.title);
  const [startsAt, setStartsAt] = useState(
    exam.startsAt ? new Date(exam.startsAt).toISOString().slice(0, 16) : ""
  );
  const [endsAt, setEndsAt] = useState(
    exam.endsAt ? new Date(exam.endsAt).toISOString().slice(0, 16) : ""
  );
  const [releasedNow, setReleasedNow] = useState(false);
  const [saving, setSaving] = useState(false);
  const lockedAfterStart = phaseOf(exam) !== "lobby";

  async function go() {
    setSaving(true);
    try {
      const patch: any = { title: title.trim() };
      if (!lockedAfterStart) {
        if (startsAt) patch.startsAt = new Date(startsAt).toISOString();
        if (endsAt) patch.endsAt = new Date(endsAt).toISOString();
      }
      if (releasedNow) patch.releasedAt = new Date().toISOString();
      await api.patchExam(exam.id, patch);
      editing.value = null;
      await load();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setSaving(false);
    }
  }
  return (
    <Modal title={`Editar: ${exam.title}`} onClose={() => (editing.value = null)}>
      <Field label="Título">
        <input
          type="text"
          value={title}
          onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
          aria-label="Título"
          class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
        />
      </Field>
      {!lockedAfterStart ? (
        <div class="grid grid-cols-2 gap-2">
          <Field label="Início">
            <input
              type="datetime-local"
              value={startsAt}
              onInput={(e) => setStartsAt((e.target as HTMLInputElement).value)}
              aria-label="Início"
              class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
            />
          </Field>
          <Field label="Fim">
            <input
              type="datetime-local"
              value={endsAt}
              onInput={(e) => setEndsAt((e.target as HTMLInputElement).value)}
              aria-label="Fim"
              class="w-full rounded-md border border-border bg-background px-3 py-1.5 text-sm outline-none focus:border-primary"
            />
          </Field>
        </div>
      ) : (
        <p class="rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-700 dark:text-amber-300">
          ⚠ Prova já iniciou — datas/banco/título avançado estão congelados.
        </p>
      )}
      {phaseOf(exam) === "closed" && (
        <label class="flex items-center gap-2 rounded-md border border-violet-500/30 bg-violet-500/5 px-3 py-2 text-xs">
          <Switch
            checked={releasedNow}
            onCheckedChange={(v) => setReleasedNow(v)}
          />
          <span class="font-semibold">Liberar resultados agora</span>
        </label>
      )}
      <ModalFooter>
        <button
          type="button"
          onClick={() => (editing.value = null)}
          disabled={saving}
          class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={go}
          disabled={saving}
          class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-bold text-white hover:brightness-110 disabled:opacity-50"
        >
          {saving ? <Loader2 className="size-3.5 animate-spin" /> : <Save width="1em" height="1em" />}
          Salvar
        </button>
      </ModalFooter>
    </Modal>
  );
}

function DeleteModal({ exam }: { exam: ExamRow }) {
  const [busy, setBusy] = useState(false);
  async function go() {
    setBusy(true);
    try {
      await api.deleteExam(exam.id);
      deleting.value = null;
      await load();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setBusy(false);
    }
  }
  return (
    <Modal title="Excluir prova" onClose={() => (deleting.value = null)}>
      <p class="text-sm text-muted-foreground">
        Vai excluir <b class="text-foreground">{exam.title}</b>. Só funciona enquanto a
        prova não começou.
      </p>
      <ModalFooter>
        <button
          type="button"
          onClick={() => (deleting.value = null)}
          disabled={busy}
          class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent disabled:opacity-50"
        >
          Cancelar
        </button>
        <button
          type="button"
          onClick={go}
          disabled={busy}
          class="inline-flex items-center gap-1.5 rounded-md border border-red-500/50 bg-red-500/10 px-3 py-1.5 text-xs font-bold text-red-600 hover:bg-red-500/15"
        >
          {busy ? <Loader2 className="size-3.5 animate-spin" /> : <Trash2 width="1em" height="1em" />}
          Excluir
        </button>
      </ModalFooter>
    </Modal>
  );
}

function MonitorModal({ exam }: { exam: ExamRow }) {
  const [data, setData] = useState<ExamMonitor | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    async function tick() {
      try {
        const r = await api.examMonitor(exam.id);
        if (alive) setData(r);
      } catch (e) {
        if (alive) setErr(friendlyError(e));
      }
    }
    tick();
    const id = setInterval(tick, 15000);
    return () => {
      alive = false;
      clearInterval(id);
    };
  }, [exam.id]);

  return (
    <Modal title={`Cockpit: ${exam.title}`} onClose={() => (monitorOpen.value = null)} wide>
      {err && (
        <div class="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          {err}
        </div>
      )}
      {!data ? (
        <p class="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Carregando…
        </p>
      ) : (
        <>
          <div class="grid gap-2 sm:grid-cols-4">
            <Kpi label="Membros" value={data.summary.totalMembros} />
            <Kpi label="Conectaram" value={data.summary.conectaram} />
            <Kpi label="Online" value={data.summary.onlineAgora} accent="emerald" />
            <Kpi label="Finalizaram" value={data.summary.finalizaram} accent="violet" />
          </div>
          <div class="overflow-hidden rounded-md border border-border">
            <table class="w-full text-xs">
              <thead>
                <tr class="border-b border-border bg-card/60 text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                  <th class="px-2 py-1.5">Aluno</th>
                  <th class="px-2 py-1.5">Status</th>
                  <th class="px-2 py-1.5 text-right">Respondidas</th>
                  <th class="px-2 py-1.5 text-right">Nota</th>
                </tr>
              </thead>
              <tbody>
                {data.students.map((s) => {
                  const finalizou = !!s.submittedAt || s.autoFinalized;
                  return (
                    <tr key={s.userId} class="border-b border-border/60 last:border-0">
                      <td class="px-2 py-1.5">
                        <div class="font-semibold">{s.name}</div>
                        <div class="font-mono text-[10px] text-muted-foreground">{s.email}</div>
                      </td>
                      <td class="px-2 py-1.5">
                        {finalizou ? (
                          <span class="inline-flex items-center gap-1 rounded bg-violet-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-violet-700 dark:text-violet-300">
                            FINALIZOU
                          </span>
                        ) : s.online ? (
                          <span class="inline-flex items-center gap-1 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
                            ONLINE
                          </span>
                        ) : s.startedAt ? (
                          <span class="inline-flex items-center gap-1 rounded bg-amber-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
                            AUSENTE
                          </span>
                        ) : (
                          <span class="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                            NÃO ENTROU
                          </span>
                        )}
                      </td>
                      <td class="px-2 py-1.5 text-right tabular-nums font-bold">{s.answeredCount}</td>
                      <td class="px-2 py-1.5 text-right tabular-nums">
                        {s.score ?? "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p class="text-[10px] text-muted-foreground">Atualiza a cada 15s.</p>
        </>
      )}
      <ModalFooter>
        <button
          type="button"
          onClick={() => (monitorOpen.value = null)}
          class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
        >
          Fechar
        </button>
      </ModalFooter>
    </Modal>
  );
}

function AnswersModal({ exam }: { exam: ExamRow }) {
  const [data, setData] = useState<ExamAnswers | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);

  async function reload() {
    try {
      const r = await api.examAnswers(exam.id);
      setData(r);
    } catch (e) {
      setErr(friendlyError(e));
    }
  }
  useEffect(() => {
    reload();
  }, [exam.id]);

  async function anular(idx: number) {
    setBusy(idx);
    try {
      await api.anularQuestao(exam.id, idx);
      await reload();
    } catch (e) {
      void openAlert({ title: "Erro", message: friendlyError(e) });
    } finally {
      setBusy(null);
    }
  }

  const canAnnul = phaseOf(exam) === "closed" || phaseOf(exam) === "released";

  return (
    <Modal title={`Respostas: ${exam.title}`} onClose={() => (answersOpen.value = null)} wide>
      {err && (
        <div class="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs text-destructive">
          {err}
        </div>
      )}
      {!data ? (
        <p class="flex items-center gap-2 text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Carregando…
        </p>
      ) : data.questoes.length === 0 ? (
        <p class="text-sm italic text-muted-foreground">
          Snapshot do banco ainda não foi tirado. Será capturado no início da prova.
        </p>
      ) : (
        <>
          <section class="space-y-2">
            <h3 class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Por aluno
            </h3>
            <div class="overflow-hidden rounded-md border border-border">
              <table class="w-full text-xs">
                <thead>
                  <tr class="border-b border-border bg-card/60 text-left text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    <th class="px-2 py-1.5">Aluno</th>
                    <th class="px-2 py-1.5 text-right">Resp.</th>
                    <th class="px-2 py-1.5 text-right">Acertos</th>
                    <th class="px-2 py-1.5 text-right">Nota</th>
                  </tr>
                </thead>
                <tbody>
                  {data.students.map((s) => (
                    <tr key={s.userId} class="border-b border-border/60 last:border-0">
                      <td class="px-2 py-1.5">
                        <div class="font-semibold">{s.name}</div>
                        <div class="font-mono text-[10px] text-muted-foreground">{s.email}</div>
                      </td>
                      <td class="px-2 py-1.5 text-right tabular-nums">{s.answeredCount}</td>
                      <td class="px-2 py-1.5 text-right tabular-nums">{s.acertos}/{data.questoes.length}</td>
                      <td class="px-2 py-1.5 text-right tabular-nums font-bold">{s.scoreOf10.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section class="space-y-2">
            <h3 class="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Por questão
            </h3>
            {data.perQuestion.map((pq) => (
              <div
                key={pq.idx}
                class={`rounded-md border p-3 ${pq.isAnulada ? "border-amber-500/40 bg-amber-500/5" : "border-border bg-card/40"}`}
              >
                <div class="flex items-start justify-between gap-2">
                  <div class="min-w-0 flex-1">
                    <div class="flex items-center gap-2">
                      <span class="rounded bg-primary/10 px-1.5 py-0.5 font-mono text-[10px] font-bold text-primary">
                        Q{pq.idx + 1}
                      </span>
                      {pq.peso != null && (
                        <span class="rounded bg-violet-500/15 px-1.5 py-0.5 font-mono text-[10px] font-bold text-violet-700 dark:text-violet-300">
                          peso {pq.peso.toFixed(2)}
                        </span>
                      )}
                      {pq.isAnulada && (
                        <span class="rounded bg-amber-500/20 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">
                          ANULADA
                        </span>
                      )}
                      <span class="text-[10px] text-muted-foreground">
                        {pq.totalRespostas} respondentes · {pq.pctAcerto ?? "—"}% acerto
                      </span>
                    </div>
                    <p class="mt-1 text-[11px] line-clamp-2">{pq.enunciado}</p>
                    <div class="mt-2 flex flex-wrap gap-1">
                      {(pq.alternativas as string[]).map((alt, ai) => {
                        const letter = String.fromCharCode(65 + ai);
                        const slot = pq.distribuicao[letter];
                        const isCorrect = ai === pq.correctIdx;
                        return (
                          <span
                            key={ai}
                            title={alt}
                            class={`rounded px-1.5 py-0.5 font-mono text-[10px] ${
                              isCorrect
                                ? "bg-emerald-500/20 text-emerald-700 dark:text-emerald-300"
                                : "bg-accent text-muted-foreground"
                            }`}
                          >
                            {letter}: {slot?.count ?? 0}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                  {canAnnul && !pq.isAnulada && (
                    <button
                      type="button"
                      onClick={() => anular(pq.idx)}
                      disabled={busy === pq.idx}
                      title="Anular esta questão (retroativo)"
                      class="inline-flex shrink-0 items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1 text-[10px] font-bold text-amber-700 hover:bg-amber-500/20 disabled:opacity-50 dark:text-amber-300"
                    >
                      {busy === pq.idx ? (
                        <Loader2 className="size-3 animate-spin" />
                      ) : (
                        <X width="0.9em" height="0.9em" />
                      )}
                      Anular
                    </button>
                  )}
                </div>
              </div>
            ))}
          </section>
        </>
      )}
      <ModalFooter>
        <button
          type="button"
          onClick={() => (answersOpen.value = null)}
          class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
        >
          Fechar
        </button>
      </ModalFooter>
    </Modal>
  );
}

function Kpi({
  label,
  value,
  accent
}: {
  label: string;
  value: number;
  accent?: "emerald" | "violet";
}) {
  const cls =
    accent === "emerald"
      ? "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300"
      : accent === "violet"
        ? "bg-violet-500/10 text-violet-700 dark:text-violet-300"
        : "bg-card";
  return (
    <div class={`rounded-md border border-border px-3 py-2 ${cls}`}>
      <div class="text-[10px] font-bold uppercase tracking-wider opacity-80">{label}</div>
      <div class="text-xl font-bold tabular-nums text-foreground">{value}</div>
    </div>
  );
}

function Modal({
  title,
  onClose,
  children,
  wide
}: {
  title: string;
  onClose: () => void;
  children: any;
  wide?: boolean;
}) {
  return (
    <div
      class="fixed inset-0 z-50 grid place-items-center bg-background/80 p-3 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        class={`flex max-h-[92vh] w-full ${wide ? "max-w-3xl" : "max-w-lg"} flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl`}
        onClick={(e) => e.stopPropagation()}
      >
        <header class="flex items-center justify-between border-b border-border px-4 py-3">
          <h2 class="flex items-center gap-2 text-base font-bold">
            <ClipboardList width="1.2em" height="1.2em" className="text-primary" />
            {title}
          </h2>
          <button
            type="button"
            onClick={onClose}
            title="Fechar"
            aria-label="Fechar"
            class="rounded-md border border-border bg-background p-1 hover:bg-accent"
          >
            <X width="1em" height="1em" />
          </button>
        </header>
        <div class="flex-1 space-y-3 overflow-y-auto p-4">{children}</div>
      </div>
    </div>
  );
}
function Field({ label, children }: { label: string; children: any }) {
  return (
    <label class="block">
      <span class="mb-1 block text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      {children}
    </label>
  );
}
function ModalFooter({ children }: { children: any }) {
  return (
    <div class="sticky bottom-0 -mx-4 -mb-4 mt-2 flex items-center justify-end gap-2 border-t border-border bg-card px-4 py-3">
      {children}
    </div>
  );
}
